import * as THREE from 'three'
import gsap from 'gsap'

export default class Environment {
    constructor(world) {
        this.world = world
        this.scene = this.world.scene
        this.resources = this.world.stateController.resources.items
        this.debug = this.world.debug
        this.time = this.world.time
        this.camera = this.world.camera.perspectiveCamera
        this.renderer = this.world.renderer

        this.setDebug()
        this.setLights()
        this.setFlare()
        this.setFog()
    }

    setDebug() {
        if (this.debug.active) {
            this.lightFolder = this.debug.ui.addFolder('Ambient')
            this.flareFolder = this.debug.ui.addFolder('Flares')
            this.debugFolder = this.debug.ui.addFolder('Environment')
            //this.debugFolder.close()
        }
    }

    setLights() {
        //ambient parameters
        this.ambient_parameters = {
            color: '#383838',
            intensity: 8,
        }

        //Ambient light
        const ambientlight = new THREE.AmbientLight(
            this.ambient_parameters.color,
            this.ambient_parameters.intensity
        )
        this.scene.add( ambientlight );
    
        //point light parameters
        this.point_parameters = {
            color: '#ff0000',
            intensity: 200,
            distance: 6,
            decay: 5,
            height: 2,
        }

        //point light -> flare
        this.light = new THREE.PointLight(
            this.point_parameters.color,
            this.point_parameters.intensity,
            this.point_parameters.distance,
            this.point_parameters.decay,
        );
        this.scene.add(this.light)

        /////////////////////////////////////////////////////////
        /////////////////////////////////////////////////////////
        /////////////////////////////////////////////////////////

        // positioning the light on POIs
        var settings = {
            options: ([4.5, 1.5, 2]),
        }

        this.light.position.set( settings.options[0], settings.options[1], settings.options[2] )

        if (this.debug.active) {
            this.flareFolder.add( settings, 'options',
            [
                ([4.5, 1.5, 2]),  
                ([-4.5, 1.5, -1.4]),
                ([5.1, 1.5, -13]),
                ([1, 1.5, -13.5]),
                ([-6.7, 1.5, -6.7]),
                ([-4, 1.5, 3]),
                ([-1.2, 1.5, 3]),
                ([3, 1.5, 11]),
            ])
            .name('Active POI')
            .onChange(() =>
            {
                this.light.position.set( settings.options[0], settings.options[1], settings.options[2] )
            })
        }
        /////////////////////////////////////////////////////////
        /////////////////////////////////////////////////////////
        /////////////////////////////////////////////////////////
        
        this.scene.add( this.light );
        this.light.castShadow = true
        this.light.shadow.mapSize.width = 1024
        this.light.shadow.mapSize.height = 1024

        this.light.shadow.camera.near = 0.5
        this.light.shadow.camera.far = 0.51
        const cameraHelper = new THREE.CameraHelper(this.light.shadow.camera)
        this.scene.add(cameraHelper)
        cameraHelper.visible = false

        //point light helper
        const sphereSize = 1;
        const pointLightHelper = new THREE.PointLightHelper( this.light, sphereSize );
        pointLightHelper.visible = false
        this.scene.add( pointLightHelper )

        //debug
        if (this.debug.active) {
            this.flareFolder.add(this.light, 'visible').name('Flare')
            
            this.flareFolder.add(pointLightHelper, 'visible').name('Flare helper')
            this.flareFolder.add(this.light, 'castShadow').name('Flare shadow')
            this.flareFolder.add(cameraHelper, 'visible').name('Flare shadow helper')

            //point color
            this.flareFolder.addColor(this.point_parameters, 'color').name('Flare color')
            .onChange(() =>
            {
                this.light.color.set(this.point_parameters.color)
            })

            //point intensity
            this.flareFolder.add(this.point_parameters, 'intensity').name('Flare intensity')
            .min(0)
            .max(500)
            .step(10)          
            .onChange(() =>
            {
                this.light.intensity = this.point_parameters.intensity
            })

            //point distance
            this.flareFolder.add(this.point_parameters, 'distance').name('Flare distance')
            .min(0.1)
            .max(10)
            .step(0.1)          
            .onChange(() =>
            {
                this.light.distance = this.point_parameters.distance
            })

            //ambient color
            this.lightFolder.addColor(this.ambient_parameters, 'color').name('Ambient color')
            .onChange(() =>
            {
                ambientlight.color.set(this.ambient_parameters.color)
            })

            //ambient strength
            this.lightFolder.add(this.ambient_parameters, 'intensity').name('Ambient intensity')
            .min(0)
            .max(20)
            .step(0.1)          
            .onChange(() =>
            {
                ambientlight.intensity = this.ambient_parameters.intensity
            })
        }
    }

    setFlare() {
        this.flareTexture = this.resources.flareTexture
        this.flareTexture.encoding = THREE.sRGBEncoding

        this.flareMaterial = new THREE.MeshBasicMaterial({
            map: this.flareTexture,
            transparent: true,
            //fog: false,
        })

        this.flareGeometry = new THREE.PlaneGeometry( 1.3, 1.3 )
        this.flare = new THREE.Mesh( this.flareGeometry, this.flareMaterial )
        this.scene.add( this.flare )
    }

    setFog() {
        //Setting fog
        this.fog_parameters = {
            color: '#1e2b3e',
            density: 0.05,
            near: 2,
            far: 25
        }

        //TWO TYPES OF FOG
        this.scene.fog = new THREE.Fog(this.fog_parameters.color, this.fog_parameters.near, this.fog_parameters.far)
        //this.scene.fog = new THREE.FogExp2(this.fog_parameters.color, this.fog_parameters.density)

        this.scene.fog.visible = false

        //FOG DEBUG
        if (this.debug.active) {
            this.flareFolder.add(this.flare, 'visible').name('Flare texture')
            //fog color
            this.debugFolder.addColor(this.fog_parameters, 'color').name('Fog color')
                .onChange(() =>
                {
                    this.scene.fog.color.set(this.fog_parameters.color)
                    this.world.renderer.instance.setClearColor(this.fog_parameters.color)
                })
        }
    }

    update() {
        //mapping the fog
        var y_zoom = this.camera.position.y;
        //fog near
        var fog_near_map = gsap.utils.mapRange(2, 24, 3, 20, y_zoom)
        this.scene.fog.near = fog_near_map
        //fog far
        var fog_far_map = gsap.utils.mapRange(2, 24, 10, 75, y_zoom)
        this.scene.fog.far = fog_far_map

        this.flare.position.copy(this.light.position)
        this.flare.quaternion.slerp(this.camera.quaternion, 0.1)

        //setting up dynamic quility
        if (this.renderer.high === true) {

            //ligth
            this.light.castShadow = true

            //light flickering
            var decay_time = this.time.elapsed * 0.002
            var decay_cos = gsap.utils.mapRange(-1, 1, 3, 6, Math.cos(decay_time))
            this.light.decay = decay_cos

            //flare texture position
            var y_time = this.time.elapsed * 0.001
            var y_cos = gsap.utils.mapRange(-1, 1, 1, 1.2, Math.cos(y_time))
            this.flare.position.y = y_cos - 0.5

        } if (this.renderer.medium === true) {

            //light
            this.light.castShadow = false

            //flare texture position
            var y_time = this.time.elapsed * 0.001
            var y_cos = gsap.utils.mapRange(-1, 1, 1, 1.2, Math.cos(y_time))
            this.flare.position.y = y_cos - 0.5

        } if (this.renderer.low === true) {
            this.light.intensity = 0
            this.flare.position.y = 0.5
        }
    }
}